import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { toast } from 'sonner';
import { Mail, Server, Lock, CheckCircle, AlertCircle, Building, MapPin, Phone, Bell, Save, Globe, Eye, EyeOff, Key, ShieldCheck, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [emailConfig, setEmailConfig] = useState(null);
  const [savingGmail, setSavingGmail] = useState(false);
  const [savingMicrosoft, setSavingMicrosoft] = useState(false);
  const [savingCompanyInfo, setSavingCompanyInfo] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isOrgOwner = currentUser.is_org_owner;
  const hasManageOrgPermission = (currentUser.permissions || []).includes('manage_organization');
  const isGoogleUser = currentUser.auth_provider === 'google';
  const hasPassword = currentUser.has_password;
  
  // Check if user is Super Admin
  // Super Admin is identified by: is_org_owner flag, manage_organization permission, or role_names containing "Super Admin"
  const userRoleNames = currentUser.role_names || [];
  const isSuperAdmin = isOrgOwner || 
    hasManageOrgPermission || 
    userRoleNames.includes('Super Admin') ||
    userRoleNames.includes('Full Access (Legacy)');
  
  // Organization Name
  const [organizationName, setOrganizationName] = useState(currentUser.organization_name || '');
  const [savingOrgName, setSavingOrgName] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Gmail SMTP
  const [gmailData, setGmailData] = useState({
    email: '',
    smtp_password: '',
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
  });
  
  // Microsoft Office 365 SMTP
  const [microsoftData, setMicrosoftData] = useState({
    ms_email: '',
    ms_smtp_password: '',
    ms_smtp_server: 'smtp.office365.com',
    ms_smtp_port: 587,
  });
  
  // Company Information
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    company_address: '',
    procurement_contact: '',
  });
  
  // Email Preferences
  const [emailPreferences, setEmailPreferences] = useState({
    send_regret_emails: true,
    enable_requote_notifications: false,
  });
  
  // PR Notification Emails
  const [prNotificationEmails, setPrNotificationEmails] = useState([]);
  const [newPrEmail, setNewPrEmail] = useState('');
  const [savingPrEmails, setSavingPrEmails] = useState(false);
  
  // PR Approval Settings
  const [prApprovalRequired, setPrApprovalRequired] = useState(false);
  const [savingPrApproval, setSavingPrApproval] = useState(false);
  
  // Active Provider
  const [activeProvider, setActiveProvider] = useState('gmail');

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      const response = await apiClient.get('/settings/email');
      setEmailConfig(response.data);
      
      if (response.data.is_configured || response.data.gmail_configured || response.data.ms_configured) {
        // Gmail settings
        setGmailData({
          email: response.data.email || '',
          smtp_password: '',
          smtp_server: response.data.smtp_server || 'smtp.gmail.com',
          smtp_port: response.data.smtp_port || 587,
        });
        
        // Microsoft settings
        setMicrosoftData({
          ms_email: response.data.ms_email || '',
          ms_smtp_password: '',
          ms_smtp_server: response.data.ms_smtp_server || 'smtp.office365.com',
          ms_smtp_port: response.data.ms_smtp_port || 587,
        });
        
        // Company info
        setCompanyInfo({
          company_name: response.data.company_name || '',
          company_address: response.data.company_address || '',
          procurement_contact: response.data.procurement_contact || '',
        });
        
        // Email preferences
        setEmailPreferences({
          send_regret_emails: response.data.send_regret_emails !== false,
          enable_requote_notifications: response.data.enable_requote_notifications || false,
        });
        
        // PR notification emails
        setPrNotificationEmails(response.data.pr_notification_emails || []);
        
        // Active provider
        setActiveProvider(response.data.active_provider || 'gmail');
      }
      
      // Fetch organization settings for PR approval
      try {
        const orgSettingsRes = await apiClient.get('/organization/settings');
        setPrApprovalRequired(orgSettingsRes.data.pr_approval_required || false);
      } catch (e) {
        console.log('Could not fetch org settings');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching email config:', error);
      setLoading(false);
    }
  };

  const handleSavePrApproval = async (value) => {
    setSavingPrApproval(true);
    try {
      await apiClient.patch('/organization/settings', { pr_approval_required: value });
      setPrApprovalRequired(value);
      toast.success(value ? 'PR approval enabled' : 'PR approval disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update setting');
    } finally {
      setSavingPrApproval(false);
    }
  };

  const handleSaveGmail = async (e) => {
    e.preventDefault();
    if (!gmailData.smtp_password) {
      toast.error('Please enter your Gmail App Password');
      return;
    }
    
    setSavingGmail(true);
    try {
      await apiClient.post('/settings/email/gmail', {
        email: gmailData.email,
        smtp_password: gmailData.smtp_password,
        smtp_server: gmailData.smtp_server,
        smtp_port: parseInt(gmailData.smtp_port),
      });
      toast.success('Gmail SMTP configured successfully');
      setGmailData(prev => ({ ...prev, smtp_password: '' }));
      fetchEmailConfig();
    } catch (error) {
      console.error('Error saving Gmail config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Gmail settings');
    } finally {
      setSavingGmail(false);
    }
  };

  const handleSaveMicrosoft = async (e) => {
    e.preventDefault();
    if (!microsoftData.ms_smtp_password) {
      toast.error('Please enter your Microsoft App Password');
      return;
    }
    
    setSavingMicrosoft(true);
    try {
      await apiClient.post('/settings/email/microsoft', {
        ms_email: microsoftData.ms_email,
        ms_smtp_password: microsoftData.ms_smtp_password,
        ms_smtp_server: microsoftData.ms_smtp_server,
        ms_smtp_port: parseInt(microsoftData.ms_smtp_port),
      });
      toast.success('Microsoft Office 365 SMTP configured successfully');
      setMicrosoftData(prev => ({ ...prev, ms_smtp_password: '' }));
      fetchEmailConfig();
    } catch (error) {
      console.error('Error saving Microsoft config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Microsoft settings');
    } finally {
      setSavingMicrosoft(false);
    }
  };

  const handleSaveCompanyInfo = async () => {
    setSavingCompanyInfo(true);
    try {
      await apiClient.patch('/settings/company-info', companyInfo);
      toast.success('Company information saved successfully');
      fetchEmailConfig();
    } catch (error) {
      console.error('Error saving company info:', error);
      toast.error(error.response?.data?.detail || 'Failed to save company information');
    } finally {
      setSavingCompanyInfo(false);
    }
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    try {
      await apiClient.patch('/settings/email-preferences', null, {
        params: {
          send_regret_emails: emailPreferences.send_regret_emails,
          enable_requote_notifications: emailPreferences.enable_requote_notifications,
        }
      });
      toast.success('Email preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(error.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleAddPrEmail = () => {
    if (!newPrEmail || !newPrEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (prNotificationEmails.includes(newPrEmail)) {
      toast.error('This email is already in the list');
      return;
    }
    setPrNotificationEmails([...prNotificationEmails, newPrEmail]);
    setNewPrEmail('');
  };

  const handleRemovePrEmail = (email) => {
    setPrNotificationEmails(prNotificationEmails.filter(e => e !== email));
  };

  const handleSavePrNotificationEmails = async () => {
    setSavingPrEmails(true);
    try {
      await apiClient.patch('/settings/pr-notifications', {
        pr_notification_emails: prNotificationEmails
      });
      toast.success('PR notification emails saved successfully');
    } catch (error) {
      console.error('Error saving PR notification emails:', error);
      toast.error(error.response?.data?.detail || 'Failed to save PR notification emails');
    } finally {
      setSavingPrEmails(false);
    }
  };

  const handleSetActiveProvider = async (provider) => {
    try {
      await apiClient.patch('/settings/email/active-provider', null, {
        params: { provider }
      });
      setActiveProvider(provider);
      toast.success(`Active email provider set to ${provider === 'gmail' ? 'Gmail' : 'Microsoft Office 365'}`);
    } catch (error) {
      console.error('Error setting active provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to set active provider');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSavingPassword(true);
    try {
      await apiClient.post('/auth/change-password', {
        current_password: passwordData.current_password || null,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      // Update localStorage to reflect that user now has a password
      const updatedUser = { ...currentUser, has_password: true };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveOrganizationName = async () => {
    if (!organizationName.trim()) {
      toast.error('Organization name cannot be empty');
      return;
    }
    
    setSavingOrgName(true);
    try {
      await apiClient.patch('/organization', { name: organizationName.trim() });
      toast.success('Organization name updated successfully');
      
      // Update localStorage
      const updatedUser = { ...currentUser, organization_name: organizationName.trim() };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Force a page reload to update sidebar
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization name');
    } finally {
      setSavingOrgName(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="settings-loading">Loading settings...</div>;
  }

  const isAnyEmailConfigured = emailConfig?.gmail_configured || emailConfig?.ms_configured || emailConfig?.is_configured;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="settings-page">
      {/* Section 0: Change/Set Password - Show for all users */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Key size={24} className="text-slate-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                {hasPassword ? 'Change Password' : 'Set Password'}
              </h2>
              <p className="text-sm text-slate-500">
                {hasPassword ? 'Update your account password' : 'Add password login to your account'}
              </p>
            </div>
          </div>
        </div>
        <form onSubmit={handleChangePassword} className="p-6 space-y-4">
          {isGoogleUser && !hasPassword && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <AlertCircle size={14} className="inline mr-2" />
                You signed up with Google. Setting a password will allow you to also log in with email and password.
              </p>
            </div>
          )}
          {hasPassword && (
            <div>
              <Label htmlFor="current_password" className="flex items-center gap-2">
                <Lock size={16} />
                Current Password *
              </Label>
              <div className="relative mt-1">
                <Input
                  id="current_password"
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="new_password" className="flex items-center gap-2">
              <Lock size={16} />
              New Password *
            </Label>
            <div className="relative mt-1">
              <Input
                id="new_password"
                type={showPasswords ? "text" : "password"}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
                className="pr-10"
              />
              {!hasPassword && (
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="confirm_password" className="flex items-center gap-2">
              <Lock size={16} />
              Confirm New Password *
            </Label>
            <Input
              id="confirm_password"
              type={showPasswords ? "text" : "password"}
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              required
              minLength={6}
              className="mt-1"
            />
          </div>
          <div className="pt-2">
            <Button type="submit" disabled={savingPassword} className="w-full">
              <Save size={16} className="mr-2" />
              {savingPassword ? 'Saving...' : hasPassword ? 'Change Password' : 'Set Password'}
            </Button>
          </div>
        </form>
      </div>

      {/* Organization Name Section - For Super Admin */}
      {isSuperAdmin && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Building size={24} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Organization Name</h2>
                <p className="text-sm text-slate-500">This name appears in the sidebar and on documents</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <Label htmlFor="org_name" className="flex items-center gap-2">
                <Building size={16} />
                Organization Name
              </Label>
              <Input
                id="org_name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Your Organization Name"
                className="mt-1"
              />
            </div>
            <div className="pt-2">
              <Button onClick={handleSaveOrganizationName} disabled={savingOrgName} className="w-full">
                <Save size={16} className="mr-2" />
                {savingOrgName ? 'Saving...' : 'Update Organization Name'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Section 1: Gmail SMTP Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Mail size={24} className="text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Gmail SMTP Configuration</h2>
                <p className="text-sm text-slate-500">Configure Gmail for sending RFQ emails</p>
              </div>
            </div>
            {emailConfig?.gmail_configured && (
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm text-green-600 font-medium">Configured</span>
                {activeProvider === 'gmail' && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">ACTIVE</span>
                )}
              </div>
            )}
          </div>
        </div>
        <form onSubmit={handleSaveGmail} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="gmail_email" className="flex items-center gap-2">
                <Mail size={16} />
                Gmail Email Address *
              </Label>
              <Input
                id="gmail_email"
                type="email"
                value={gmailData.email}
                onChange={(e) => setGmailData({ ...gmailData, email: e.target.value })}
                placeholder="your-email@gmail.com"
                required
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="gmail_password" className="flex items-center gap-2">
                <Lock size={16} />
                Gmail App Password *
              </Label>
              <Input
                id="gmail_password"
                type="password"
                value={gmailData.smtp_password}
                onChange={(e) => setGmailData({ ...gmailData, smtp_password: e.target.value })}
                placeholder="16-character app password"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Not your regular Gmail password. <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Generate App Password</a>
              </p>
            </div>
            <div>
              <Label htmlFor="gmail_server" className="flex items-center gap-2">
                <Server size={16} />
                SMTP Server
              </Label>
              <Input
                id="gmail_server"
                value={gmailData.smtp_server}
                onChange={(e) => setGmailData({ ...gmailData, smtp_server: e.target.value })}
                placeholder="smtp.gmail.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="gmail_port">SMTP Port</Label>
              <Input
                id="gmail_port"
                type="number"
                value={gmailData.smtp_port}
                onChange={(e) => setGmailData({ ...gmailData, smtp_port: e.target.value })}
                placeholder="587"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={savingGmail} className="flex-1">
              {savingGmail ? 'Saving...' : (emailConfig?.gmail_configured ? 'Update Gmail Settings' : 'Save Gmail Settings')}
            </Button>
            {emailConfig?.gmail_configured && activeProvider !== 'gmail' && (
              <Button type="button" variant="outline" onClick={() => handleSetActiveProvider('gmail')}>
                Set as Active
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Section 2: Microsoft Office 365 SMTP Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Microsoft Office 365 SMTP Configuration</h2>
                <p className="text-sm text-slate-500">Configure Outlook/Office 365 for sending RFQ emails</p>
              </div>
            </div>
            {emailConfig?.ms_configured && (
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm text-green-600 font-medium">Configured</span>
                {activeProvider === 'microsoft' && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">ACTIVE</span>
                )}
              </div>
            )}
          </div>
        </div>
        <form onSubmit={handleSaveMicrosoft} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="ms_email" className="flex items-center gap-2">
                <Mail size={16} />
                Microsoft Email Address *
              </Label>
              <Input
                id="ms_email"
                type="email"
                value={microsoftData.ms_email}
                onChange={(e) => setMicrosoftData({ ...microsoftData, ms_email: e.target.value })}
                placeholder="your-email@outlook.com or your-email@company.com"
                required
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="ms_password" className="flex items-center gap-2">
                <Lock size={16} />
                Microsoft App Password *
              </Label>
              <Input
                id="ms_password"
                type="password"
                value={microsoftData.ms_smtp_password}
                onChange={(e) => setMicrosoftData({ ...microsoftData, ms_smtp_password: e.target.value })}
                placeholder="App password or account password"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                For Office 365, you may need to enable SMTP AUTH or create an app password.
              </p>
            </div>
            <div>
              <Label htmlFor="ms_server" className="flex items-center gap-2">
                <Server size={16} />
                SMTP Server
              </Label>
              <Input
                id="ms_server"
                value={microsoftData.ms_smtp_server}
                onChange={(e) => setMicrosoftData({ ...microsoftData, ms_smtp_server: e.target.value })}
                placeholder="smtp.office365.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="ms_port">SMTP Port</Label>
              <Input
                id="ms_port"
                type="number"
                value={microsoftData.ms_smtp_port}
                onChange={(e) => setMicrosoftData({ ...microsoftData, ms_smtp_port: e.target.value })}
                placeholder="587"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={savingMicrosoft} className="flex-1">
              {savingMicrosoft ? 'Saving...' : (emailConfig?.ms_configured ? 'Update Microsoft Settings' : 'Save Microsoft Settings')}
            </Button>
            {emailConfig?.ms_configured && activeProvider !== 'microsoft' && (
              <Button type="button" variant="outline" onClick={() => handleSetActiveProvider('microsoft')}>
                Set as Active
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Section 3: Company Information - Super Admin Only */}
      {isSuperAdmin && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building size={24} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">Company Information</h2>
              <p className="text-sm text-slate-500">This information will appear in RFQ emails sent to suppliers</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label htmlFor="company_name" className="flex items-center gap-2">
              <Building size={16} />
              Company Name
            </Label>
            <Input
              id="company_name"
              value={companyInfo.company_name}
              onChange={(e) => setCompanyInfo({ ...companyInfo, company_name: e.target.value })}
              placeholder="Your Company Name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="company_address" className="flex items-center gap-2">
              <MapPin size={16} />
              Company Address
            </Label>
            <Textarea
              id="company_address"
              value={companyInfo.company_address}
              onChange={(e) => setCompanyInfo({ ...companyInfo, company_address: e.target.value })}
              placeholder="Full company address"
              rows={2}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="procurement_contact" className="flex items-center gap-2">
              <Phone size={16} />
              Procurement Team Contact Number
            </Label>
            <Input
              id="procurement_contact"
              value={companyInfo.procurement_contact}
              onChange={(e) => setCompanyInfo({ ...companyInfo, procurement_contact: e.target.value })}
              placeholder="+91 XXXXXXXXXX"
              className="mt-1"
            />
          </div>
          <div className="pt-2">
            <Button onClick={handleSaveCompanyInfo} disabled={savingCompanyInfo} className="w-full">
              <Save size={16} className="mr-2" />
              {savingCompanyInfo ? 'Saving...' : 'Save Company Information'}
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* Section 4: Email Preferences - Super Admin Only */}
      {isSuperAdmin && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Bell size={24} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">Email Preferences</h2>
              <p className="text-sm text-slate-500">Configure automated email notifications</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {!isAnyEmailConfigured && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle size={16} className="inline mr-2" />
                Please configure Gmail or Microsoft SMTP settings first to enable email preferences.
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Regret Email Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium text-slate-900">Send Regret Emails</Label>
                <p className="text-xs text-slate-500">Automatically notify suppliers who lost the order when you award</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailPreferences.send_regret_emails}
                  onChange={(e) => setEmailPreferences({ ...emailPreferences, send_regret_emails: e.target.checked })}
                  className="sr-only peer"
                  disabled={!isAnyEmailConfigured}
                  data-testid="send-regret-emails-toggle"
                />
                <div className={`w-11 h-6 ${isAnyEmailConfigured ? 'bg-slate-300' : 'bg-slate-200 cursor-not-allowed'} peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600`}></div>
              </label>
            </div>

            {/* Re-Quote Notification System Toggle */}
            <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <RefreshCw size={18} className="text-amber-600" />
                  <Label className="text-sm font-medium text-slate-900">Re-Quote Notification System</Label>
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">BETA</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  When enabled, automatically sends email notifications to non-L1 suppliers (L2, L3, etc.) 
                  encouraging them to revise their quotes. Suppliers see their rank and price gap from L1.
                </p>
                <ul className="text-xs text-slate-500 mt-2 ml-4 list-disc space-y-1">
                  <li>Triggers when at least 2 quotes are received</li>
                  <li>Supplier names & L1 price kept confidential</li>
                  <li>Maintains version history of all re-quotes</li>
                </ul>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={emailPreferences.enable_requote_notifications}
                  onChange={(e) => setEmailPreferences({ ...emailPreferences, enable_requote_notifications: e.target.checked })}
                  className="sr-only peer"
                  disabled={!isAnyEmailConfigured}
                  data-testid="enable-requote-notifications-toggle"
                />
                <div className={`w-11 h-6 ${isAnyEmailConfigured ? 'bg-slate-300' : 'bg-slate-200 cursor-not-allowed'} peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500`}></div>
              </label>
            </div>
          </div>

          <div className="pt-4 mt-4 border-t border-slate-200">
            <Button 
              onClick={handleSavePreferences}
              disabled={!isAnyEmailConfigured || savingPreferences}
              className="w-full"
              data-testid="save-email-preferences-btn"
            >
              <Save size={16} className="mr-2" />
              {savingPreferences ? 'Saving...' : 'Save Email Preferences'}
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* Section 5: PR Notification Emails - Super Admin Only */}
      {isSuperAdmin && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Mail size={24} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">PR Notification Emails</h2>
              <p className="text-sm text-slate-500">Managers to notify when new Purchase Requisitions are submitted</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {!isAnyEmailConfigured && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle size={16} className="inline mr-2" />
                Please configure Gmail or Microsoft SMTP settings first to enable PR notifications.
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Add Email Input */}
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="manager@company.com"
                value={newPrEmail}
                onChange={(e) => setNewPrEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPrEmail())}
                disabled={!isAnyEmailConfigured}
                className="flex-1"
                data-testid="pr-notification-email-input"
              />
              <Button 
                type="button"
                onClick={handleAddPrEmail}
                disabled={!isAnyEmailConfigured || !newPrEmail}
                data-testid="add-pr-notification-email-btn"
              >
                Add
              </Button>
            </div>
            
            {/* Email Tags */}
            {prNotificationEmails.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg min-h-[60px]">
                {prNotificationEmails.map((email, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                    data-testid={`pr-notification-email-tag-${idx}`}
                  >
                    <Mail size={14} />
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemovePrEmail(email)}
                      className="ml-1 text-indigo-600 hover:text-indigo-900"
                      data-testid={`remove-pr-notification-email-${idx}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-slate-500">No notification emails configured</p>
                <p className="text-xs text-slate-400 mt-1">Add manager emails to receive PR notifications</p>
              </div>
            )}
            
            <p className="text-xs text-slate-500">
              These managers will receive an email whenever a new Purchase Requisition is submitted.
            </p>
          </div>

          <div className="pt-4 mt-4 border-t border-slate-200">
            <Button 
              onClick={handleSavePrNotificationEmails}
              disabled={!isAnyEmailConfigured || savingPrEmails}
              className="w-full"
              data-testid="save-pr-notification-emails-btn"
            >
              <Save size={16} className="mr-2" />
              {savingPrEmails ? 'Saving...' : 'Save PR Notification Emails'}
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* Section 6: PR Approval Workflow - Super Admin Only */}
      {isSuperAdmin && (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <ShieldCheck size={24} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">PR Approval Workflow</h2>
              <p className="text-sm text-slate-500">Require Super Admin approval for Purchase Requisitions</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-slate-900">Enable PR Approval</Label>
              <p className="text-xs text-slate-500 mt-1">
                When enabled, PRs must be approved by Super Admin before they can be converted to RFQs
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={prApprovalRequired}
                onChange={(e) => handleSavePrApproval(e.target.checked)}
                className="sr-only peer"
                disabled={savingPrApproval}
                data-testid="pr-approval-toggle"
              />
              <div className={`w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500`}></div>
            </label>
          </div>
          
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600">
              <strong>How it works:</strong><br/>
              • PRs will have "Pending Approval" status when submitted<br/>
              • Super Admins can approve or reject PRs<br/>
              • Only approved PRs can be converted to RFQs
            </p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Settings;
